import { useNavigate } from "react-router-dom";
import { type PropsWithChildren } from "react";

import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { basketActions } from "~/providers/store/basket";
import {
  employeeActions,
  useEmployeeIdentifier
} from "~/providers/store/employee";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import {
  useCollectFlow,
  useCollectSuccessInfo
} from "~/providers/store/collect/hooks";
import { useCollectSuccessHandler } from "~/api/collect/hooks/use-collect-order-lines";
import { collectActions } from "~/providers/store/collect/actions";
import { useInvalidateProducts } from "~/api/products/hooks/use-invalidate-products";

import { DialogLoading } from "../DialogLoading";

import { DialogPaymentError } from "./DialogPaymentError";
import { DialogPaymentSuccess } from "./DialogPaymentSuccess";

type DialogPaymentProps = PropsWithChildren<
  | {
      status: "error" | "cancelled";
      orderId?: string;
      change?: number;
      receiptList?: never;
    }
  | {
      status: "success";
      receiptList?: string[];
      orderId: string;
      change?: number;
    }
>;

export const DialogPayment = ({
  status,
  orderId,
  change,
  children,
  receiptList
}: DialogPaymentProps) => {
  const navigate = useNavigate();

  const { data: till } = useCurrentTill();
  const employeeIdentifier = useEmployeeIdentifier();
  const isAttended = hasAttendedSellConfig(till);

  const handleCollectSuccess = useCollectSuccessHandler();
  const successInfo = useCollectSuccessInfo();
  const collectFlow = useCollectFlow();

  const invalidateProducts = useInvalidateProducts();

  const handleClose = () => {
    invalidateProducts(employeeIdentifier?.value);

    if (successInfo) {
      // redirect is handled by handleCollectSuccess if a collection occurred
      handleCollectSuccess({
        collectedOrderLines: successInfo.collectedOrderLines,
        additionalProductsPurchased: successInfo.additionalProductsPurchased,
        isFullyCollected: successInfo.isFullyCollected,
        forceRedirect: true
      });
    } else {
      // if the user arrives from collect, but didn't collect any products,
      // they should be redirected to the collect page
      const redirect = collectFlow === "pos" ? "/pos/sell" : "/collect";

      if (
        till?.type === "self-service" ||
        (till?.type === "attended" && till.config.resetDiningOption)
      ) {
        basketActions.resetDiningOption();
      }

      basketActions.reset();
      employeeActions.reset();
      collectActions.reset();

      navigate(redirect);
    }
  };

  if (status === "success") {
    return (
      <DialogPaymentSuccess
        change={change}
        onTimeout={handleClose}
        isAttended={isAttended}
        onClose={handleClose}
        orderId={orderId}
        receiptList={receiptList}
      />
    );
  }

  if (status === "error" || status === "cancelled")
    return (
      <DialogPaymentError
        isAttended={isAttended}
        onClose={() => {
          navigate(isAttended ? "/pos/sell" : "/pos");
        }}
        status={status}
      >
        {children}
      </DialogPaymentError>
    );

  return <DialogLoading />;
};
